import { format, parseISO, subDays } from "date-fns";
import { ParentHistoryEntry } from "../types";

/**
 * The day before a change takes effect, which is the last day the old
 * parent prices the account.
 *
 * Kept in local time throughout. Stepping back a day and then reading the
 * date off toISOString would cross midnight back into the previous day
 * everywhere east of Greenwich.
 */
export const dayBefore = (isoDate: string): string => {
  const date = parseISO(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }
  return format(subDays(date, 1), "yyyy-MM-dd");
};

/** "#1", "#1 and #2", "#1, #2 and #3". */
const listParents = (parentNos: string[]): string => {
  const tagged = parentNos.map((no) => `#${no}`);
  return tagged.length > 1
    ? `${tagged.slice(0, -1).join(", ")} and ${tagged[tagged.length - 1]}`
    : tagged[0];
};

/**
 * What the change does to pricing, said plainly enough to be read before
 * saving. Prices cascade from the parent, so the date decides which parent
 * prices which days, and an account with no parent prices itself.
 *
 * Every parent with days between today and the new start is named, in
 * order, so one already lined up ahead of the new parent is not left out.
 */
export const effectText = ({
  history,
  today,
  newParentNo,
  validFrom,
}: {
  history: ParentHistoryEntry[];
  today: string;
  newParentNo: string;
  validFrom: string;
}): string => {
  const parent = newParentNo ? `#${newParentNo}` : "the new parent";
  const until = dayBefore(validFrom);

  const parentsBefore = history
    .filter(
      (entry) =>
        entry.validFrom <= until &&
        (entry.validTo === null || entry.validTo >= today)
    )
    .sort((a, b) => a.validFrom.localeCompare(b.validFrom))
    .map((entry) => entry.parentCustomerNo);

  if (parentsBefore.length) {
    return (
      `Until ${until} prices still cascade from ` +
      `${listParents(
        parentsBefore
      )}. From ${validFrom} they come from ${parent}. ` +
      `Exceptions on this account are kept.`
    );
  }

  return (
    `Until ${until} this account keeps pricing itself. ` +
    `From ${validFrom} prices cascade from ${parent}. Exceptions on this ` +
    `account are kept.`
  );
};
